import React from "react"
import { twMerge } from "tailwind-merge"
import Typography from "aldoo-ra/Typography"
import Spinner from "../ui/Spinner"
import { formatMoney as formatStoreMoney } from "aldoo-ra/Store/currency"

export function Section({ title, children, actions, className = "" }) {
  return (
    <section className={twMerge("flex flex-col gap-5", className)}>
      {(title || actions) && (
        <div className="flex flex-row flex-wrap items-center justify-between gap-3">
          {title && (
            <Typography component="h2" variant="h5" className="leading-none">
              {title}
            </Typography>
          )}
          {actions}
        </div>
      )}
      {children}
    </section>
  )
}

export function Field({ label, error, children, className = "" }) {
  return (
    <label className={twMerge("flex flex-col gap-1.5", className)}>
      {label && <span className="text-[13px] font-semibold uppercase tracking-wide text-text_secondary">{label}</span>}
      {children}
      {error && <span className="text-[13px] text-error">{error}</span>}
    </label>
  )
}

// one option of a pick-one row inside a Field (currency, language…)
export function ChoiceButton({ selected, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={twMerge(
        "h-8 min-w-[64px] rounded-full border border-dark px-4 text-sm font-bold uppercase transition-colors",
        selected ? "bg-dark text-white" : "text-text hover:bg-neutral",
        className
      )}
    >
      {children}
    </button>
  )
}

const inputCls =
  "h-[46px] w-full rounded-lg border border-bordered bg-paper px-4 text-[15px] text-text outline-none transition-colors focus:border-text disabled:opacity-60"

export const Input = React.forwardRef(function Input(props, ref) {
  return <input ref={ref} {...props} className={twMerge(inputCls, props.className)} />
})

export function Select(props) {
  return <select {...props} className={twMerge(inputCls, "appearance-none", props.className)} />
}

export function Textarea(props) {
  return <textarea {...props} className={twMerge(inputCls, "h-auto min-h-[120px] py-3", props.className)} />
}

export function Card({ children, className = "", ...props }) {
  return (
    <div {...props} className={twMerge("rounded-xl border border-bordered bg-paper p-5", className)}>
      {children}
    </div>
  )
}

export function Empty({ children }) {
  return <p className="m-0 py-10 text-center text-text_secondary">{children}</p>
}

export function Loading() {
  return <Spinner className="py-16" />
}

export function ErrorText({ children }) {
  if (!children) return null
  return <p className="m-0 rounded-lg bg-error/10 px-4 py-3 text-[14px] text-error">{children}</p>
}

// Browser locale like the old `toLocaleDateString()` (account sections render client-side only)
export const formatDate = (value, locale) => (value ? new Date(value).toLocaleDateString(locale) : "")

// an amount in cents, in the currency of the record it belongs to
export const formatMoney = (amountCents, currency) => formatStoreMoney(amountCents, currency && String(currency).toUpperCase(), { cents: true, locale: "en" })
